import React from 'react';
import { Link } from 'react-router';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Bird,
  Users,
  CheckCircle2,
  MessageSquare,
  Smartphone,
  Share2,
  Layout,
  ArrowRight,
} from 'lucide-react';
import { StatsSection } from '@/components/StatsSection';

const Home: React.FC = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="container mx-auto px-4 py-16 sm:py-24 lg:py-32">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full border bg-background px-4 py-1.5 text-sm text-muted-foreground">
              <Bird className="size-4 text-primary" />
              <span>Biological observation platform</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              Better Data for{' '}
              <span className="text-primary">Better Observations</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl">
              A platform for organisations to manage biological records, members, and community engagement in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto text-base">
                <Link to="/register">
                  Get Started
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg" className="w-full sm:w-auto text-base">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Everything your organisation needs
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            From recording observations to managing members, TytoBio brings all the tools together.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <Users className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Organisation Hubs</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Dedicated spaces for your organisation with member roles, admin controls, and event coordination.
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <CheckCircle2 className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Observation Records</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Detailed records with species data, locations, photos, and localised species management.
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <MessageSquare className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Integrated Messaging</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Built-in communication tools for member inquiries and public contact requests.
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <Smartphone className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Mobile-Friendly</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Record observations directly from the field with our responsive, mobile-first interface.
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <Share2 className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Open Data API</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Share your records with third-party websites via our public-facing API endpoints.
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-muted/30">
            <CardHeader className="pb-3">
              <div className="size-10 bg-primary/10 rounded-lg flex items-center justify-center mb-2">
                <Layout className="size-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Rich Content</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Format observations, events, and notes with a built-in Markdown editor.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 pb-16">
        <StatsSection />
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-16 sm:py-20 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Ready to get started?
            </h2>
            <p className="text-primary-foreground/80 text-lg">
              Join TytoBio and start managing your organisation's records, members, and events today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button variant="secondary" size="lg" asChild className="text-base">
                <Link to="/register">Create an Account</Link>
              </Button>
              <Button variant="secondary" size="lg" asChild className="text-base">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
